import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { switchMap, takeUntil } from 'rxjs/operators';
import { PurchaseService } from '../../services/purchase-service.service';
import { PriceUnitComponent } from '../../components/price-unit/price-unit.component';
import { UiFormFieldBuilderModule } from '@digipay/ui-form-field-builder';
import { MatCheckboxModule } from '@angular/material/checkbox';

import { BaseComponent } from '../../../../components/core/components/base/base.component';
import { AppFundData } from '../../../../data-access/models/app-fund-data.model';
import { FilterModel } from '../../../funds/models/filter.model';
import { FormErrorStatus } from '../../../../data-access/models/form-error-status.model';
import { EpdfType } from '../../../../components/core/models/instruments.enum';
import { FundDataService } from '../../../../components/core/services/fund-data.service';
import { numberToString } from '../../../../components/utils/number-to-string';
import { INVESTMENT_LIST_ROUTE, PROSPECTUS_ROUTE, SELL_DETAIL_ROUTE } from '../../../../data-access/constants/app-routes';
import { IProspectusRouteState } from '../../../../components/core/models/prospectus-route-state.interface';
import { WealthNavigationService } from '@client-monorepo/wealth/navigation';
import { RouteStateService } from '@client-monorepo/common/utilities';
import { IFundProfileModel } from '../../../funds/models/fund-profile.model';
import { FundSort } from '../../../../data-access/enums/fund-sort';
import { NgxAppBarComponent } from '@digipay/ngx-app-bar';
import { NgxButtonComponent } from '@digipay/ngx-button';
import { NgxCheckboxComponent } from '@digipay/ngx-checkbox';
import { SpinnerComponent } from '../../../../shared/components/spinner/spinner.component';
import { NgTemplateOutlet } from '@angular/common';
import { DelayBottomSheetComponent } from '../../components/delay-bottom-sheet/delay-bottom-sheet.component';
import { NgxBottomSheetService } from '@digipay/ngx-bottom-sheet';
import { NgxCalloutComponent } from '@digipay/ngx-callout';

@Component({
  selector: 'app-sell',
  templateUrl: './sell.component.html',
  styleUrls: ['./sell.component.scss'],
  standalone: true,
  imports: [
    MatCheckboxModule,
    PriceUnitComponent,
    ReactiveFormsModule,
    UiFormFieldBuilderModule,
    NgxAppBarComponent,
    NgxButtonComponent,
    NgxCheckboxComponent,
    SpinnerComponent,
    NgTemplateOutlet,
    NgxCalloutComponent,
  ],
})
export class SellComponent extends BaseComponent implements OnInit {
  state: { amount?: ''; agreementChecked?: boolean; unitCount?: number } | undefined;
  showAgreementCheckbox = true;
  form: FormGroup;
  isLoading = true;
  tomanValue = '';
  availableUnits = 0;
  assetData: IFundProfileModel;
  showError: FormErrorStatus = 'hidden';
  assets!: AppFundData[];
  symbol!: string;
  agreementChecked = false;
  pdfType = EpdfType;

  filter: FilterModel<FundSort>;

  private route = inject(ActivatedRoute);
  private formBuilder = inject(FormBuilder);
  private routeState = inject(RouteStateService);
  private purchaseService = inject(PurchaseService);
  private fundDataService = inject(FundDataService);
  private navigationService = inject(WealthNavigationService);
  private bottomSheetService = inject(NgxBottomSheetService);

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.state = this.routeState.getAll();
    if (this.state?.amount) {
      this.tomanValue = numberToString((this.state?.amount * this.assetData?.saleNav) / 10);
      this.showError = 'hidden';
    }
    if (this.state?.agreementChecked) {
      this.agreementChecked = this.state?.agreementChecked;
    }
    this.form = this.formBuilder.group({
      count: [this.state?.unitCount || this.state?.amount || '', [Validators.required, Validators.min(1)]],
    });
    this.route.params
      .pipe(
        switchMap((params: Params) => {
          this.symbol = params['id'];
          return this.purchaseService.getSellableUnits(this.symbol);
        }),
        switchMap((sellable) => {
          if (sellable.success && sellable.result) {
            this.availableUnits = sellable.result;
          }
          return this.fundDataService.getFundBySymbol(this.symbol);
        }),
      )
      .subscribe((fund) => {
        if (fund?.success) {
          this.assetData = fund.result;
        }
        this.isLoading = false;

        if (!this.availableUnits) {
          // this.router.navigate([FUNDS_LIST_ROUTE, this.symbol]);
          this.showError = 'show';
        }
      });
  }

  isValid() {
    return this.availableUnits && this.form.value.count >= 1 && this.form.value.count <= this.availableUnits;
  }

  private verifySell() {
    if (this.isValid()) {
      this.navigationService.navigate([SELL_DETAIL_ROUTE], {
        state: {
          amount: this.assetData.saleNav * this.form.value.count,
          cardTitle: 'فروش',
          providerName: this.assetData.title,
          symbol: this.assetData.symbol,
          type: this.assetData.type,
          unit: +this.form.value.count,
        },
      });
    }
  }

  onShowDetail(): void {
    if (this.assetData.type === 'Gold') {
      this.bottomSheetService.openBottomSheet(DelayBottomSheetComponent, {
        data: { isPurchase: false },
      });
      this.bottomSheetService.onClose.pipe(takeUntil(this.destroyObservable)).subscribe(() => {
        const outputData = this.bottomSheetService.outputData();
        if (outputData) {
          this.showError = 'show';
          this.verifySell();
        }
      });
    }
    else{
      this.verifySell();
    }
  }

  onBackHandler(): void {
    const queryParams = {
      referrer: this.state['referrer'],
    };
    this.navigationService.navigateWithQueryParams([INVESTMENT_LIST_ROUTE, this.assetData.symbol], { queryParams: queryParams });
  }

  countChanged(val: number): void {
    if (val > this.availableUnits) {
      this.showError = 'show';
    } else {
      this.showError = 'hidden';
    }
    this.tomanValue = numberToString((val * this.assetData.saleNav) / 10);
  }

  getSellableUnits() {
    this.purchaseService
      .getSellableUnits(this.symbol)
      .pipe(takeUntil(this.destroyObservable))
      .subscribe((res) => {
        this.availableUnits = res.result;
      });
  }

  onToggleAgreement(val: any) {
    this.agreementChecked = val;
  }

  agreementView(pdfType: EpdfType) {
    const state: IProspectusRouteState = {
      symbol: this.symbol,
      backToProfile: false,
      pdfType,
      type: 'sell',
      amount: this.form.value.count,
      agreementChecked: this.agreementChecked,
    };

    this.navigationService.navigateWithState([PROSPECTUS_ROUTE], {
      state: state,
    });
  }
}
